import React, { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { sortBy } from 'lodash';
import {
    AGENT_SOFTWARE_ID,
    MANTY_STATUS,
    ORG_CHART_HR_MAPPING,
    ORG_CHART_HR_SOFTWARE_ID,
} from 'routes/HumanResourcesModule/business/definitions';
import { START_DATE } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import {
    HrSimulation,
    SimulationDateString,
} from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { formatDate } from 'helpers/dateHelper';
import ModalHeader from 'ui/Modals/ModalHeader';
import { SimulationTypeEnum } from 'routes/HumanResourcesModule/Simulation/helpers/constants';
import AgentPayComponentHypotheses from '../AgentPayComponent/AgentPayComponentHypotheses';
import NonRecurrentEnvelopes from '../NonRecurrentEnvelopes';
import EnvelopePayComponentHypotheses from '../EnvelopePayComponentHypotheses';
import { useGetDuplicableHypotheses } from './helpers/hooks/duplicableHypotheses';
import ArrivalHypotheses from '../Arrival/ArrivalHypotheses';
import DeparturesHypotheses from '../Departure/DeparturesHypotheses';
import GVTHypotheses from '../PayConstant/GVTHypotheses';
import GradeAdvancementHypotheses from '../GradeAdvancement/GradeAdvancementHypotheses';
import { DuplicationFormState } from './helpers/hooks/useDuplicationForm';

type Props = {
    hrSimulation: HrSimulation;
    selectedOrgChartHrsSoftwareIds: string[];
    isEtablissementsFilterActive: boolean;
    selectedEtablissementSoftwareIds: string[];
    isPayBudgetFilterActive: boolean;
    selectedPayBudgetSoftwareIds: string[];
    setIsDuplicableHypothesesLoading: Dispatch<SetStateAction<boolean>>;
    formState: DuplicationFormState;
    setFormState: React.Dispatch<React.SetStateAction<DuplicationFormState>>;
    simulationType: SimulationTypeEnum;
    recurrentInitialisationStartDate: SimulationDateString;
};

const StySkeleton = styled(Skeleton, {
    name: 'StySkeleton',
})(({ theme }) => ({
    br: '0.5rem',
    height: '5rem',
    backgroundColor: theme.palette.lightGrey,
}));

export default function DuplicableHypotheses({
    hrSimulation,
    selectedOrgChartHrsSoftwareIds,
    isEtablissementsFilterActive,
    selectedEtablissementSoftwareIds,
    isPayBudgetFilterActive,
    selectedPayBudgetSoftwareIds,
    setIsDuplicableHypothesesLoading,
    simulationType,
    recurrentInitialisationStartDate,
    formState,
    setFormState,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const { data: duplicableHypothesesResponse, isLoading: isDataLoading } =
        useGetDuplicableHypotheses(
            hrSimulation.id,
            selectedOrgChartHrsSoftwareIds,
            isEtablissementsFilterActive,
            selectedEtablissementSoftwareIds,
            isPayBudgetFilterActive,
            selectedPayBudgetSoftwareIds,
            simulationType,
            recurrentInitialisationStartDate,
        );

    useEffect(() => {
        setIsDuplicableHypothesesLoading(isDataLoading);
    }, [isDataLoading, setIsDuplicableHypothesesLoading]);

    const detailedAgentDepartureHypothesesData =
        sortBy(duplicableHypothesesResponse?.agentDepartureHypotheses, [
            ORG_CHART_HR_MAPPING,
            START_DATE,
        ]) || [];

    const detailedAgentArrivalHypothesesData =
        sortBy(duplicableHypothesesResponse?.agentArrivalHypotheses, [
            ORG_CHART_HR_MAPPING,
            START_DATE,
        ]) || [];
    const envelopeMantyStatusHypothesesData =
        sortBy(duplicableHypothesesResponse?.envelopeMantyStatusHypotheses, [
            MANTY_STATUS,
            ORG_CHART_HR_SOFTWARE_ID,
        ]) || [];
    const envelopePayComponentHypothesesData =
        sortBy(duplicableHypothesesResponse?.envelopePayComponentHypotheses, [
            MANTY_STATUS,
            ORG_CHART_HR_SOFTWARE_ID,
        ]) || [];
    const agentPayComponentHypothesesData =
        sortBy(duplicableHypothesesResponse?.agentPayComponentHypotheses, [
            AGENT_SOFTWARE_ID,
            ORG_CHART_HR_SOFTWARE_ID,
        ]) || [];
    const gradeAdvancementHypothesesData =
        sortBy(duplicableHypothesesResponse?.gradeAdvancementHypotheses, [
            AGENT_SOFTWARE_ID,
            ORG_CHART_HR_SOFTWARE_ID,
        ]) || [];

    const undefinedGvtHypothesis = {
        value: 0,
        defaultValue: 0,
        date: '',
    };
    const gvtHypothesesData = {
        indexPointHypothesis:
            duplicableHypothesesResponse?.indexPointHypothesis ??
            undefinedGvtHypothesis,
        indexRaiseHypothesis: duplicableHypothesesResponse?.indexRaiseHypothesis
            .value
            ? duplicableHypothesesResponse?.indexRaiseHypothesis
            : undefinedGvtHypothesis,
        rateCnraclHypothesis:
            duplicableHypothesesResponse?.rateCnraclHypothesis ??
            undefinedGvtHypothesis,
        smicHypothesis:
            duplicableHypothesesResponse?.smicHypothesis ??
            undefinedGvtHypothesis,
    };
    const freeHypotheses = duplicableHypothesesResponse?.freeHypotheses || [];

    const onInputChange = useCallback(
        (name: keyof DuplicationFormState, value: number[]) => {
            setFormState({ ...formState, [name]: value });
        },
        [JSON.stringify(formState), setFormState],
    );

    return (
        <>
            <ModalHeader
                title={t('humanResources.simulation.duplicateSimulation.title')}
                subtitle={t(
                    'humanResources.simulation.duplicateSimulation.thirdStep.subtitle',
                )}
                description={t(
                    'humanResources.simulation.duplicateSimulation.thirdStep.description',
                    {
                        startDate: formatDate(new Date(hrSimulation.startDate)),
                    },
                )}
            />
            {isDataLoading ? (
                <Box>
                    {Array.from({ length: 4 }).map((_a, index) => (
                        <StySkeleton key={index} variant="text" />
                    ))}
                </Box>
            ) : (
                <Box>
                    <GVTHypotheses
                        gvtHypotheses={gvtHypothesesData}
                        duplicateDisplay
                        freeHypotheses={freeHypotheses}
                    />
                    <ArrivalHypotheses
                        detailedAgentArrivalHypothesesData={
                            detailedAgentArrivalHypothesesData
                        }
                        excludedAgentArrivalHypotheses={
                            formState.excludedAgentArrivalHypotheses
                        }
                        setExcludedAgentArrivalHypotheses={(v) => {
                            onInputChange('excludedAgentArrivalHypotheses', v);
                        }}
                    />
                    <DeparturesHypotheses
                        detailedAgentDepartureHypothesesData={
                            detailedAgentDepartureHypothesesData
                        }
                        excludedAgentDepartureHypotheses={
                            formState.excludedAgentDepartureHypotheses
                        }
                        setExcludedAgentDepartureHypotheses={(v) => {
                            onInputChange(
                                'excludedAgentDepartureHypotheses',
                                v,
                            );
                        }}
                    />
                    <NonRecurrentEnvelopes
                        envelopeMantyStatusHypotheses={
                            envelopeMantyStatusHypothesesData
                        }
                        excludedMantyStatusHypotheses={
                            formState.excludedMantyStatusHypotheses
                        }
                        setExcludedMantyStatusHypotheses={(v) => {
                            onInputChange('excludedMantyStatusHypotheses', v);
                        }}
                    />
                    <EnvelopePayComponentHypotheses
                        hypotheses={envelopePayComponentHypothesesData}
                        excludedPayComponentHypotheses={
                            formState.excludedPayComponentHypotheses
                        }
                        setExcludedPayComponentHypotheses={(v) => {
                            onInputChange('excludedPayComponentHypotheses', v);
                        }}
                    />
                    <AgentPayComponentHypotheses
                        agentPayComponentHypotheses={
                            agentPayComponentHypothesesData
                        }
                        excludedAgentPayComponentHypotheses={
                            formState.excludedAgentPayComponentHypotheses
                        }
                        setExcludedAgentPayComponentHypotheses={(v) => {
                            onInputChange(
                                'excludedAgentPayComponentHypotheses',
                                v,
                            );
                        }}
                    />

                    <GradeAdvancementHypotheses
                        detailedGradeAdvancementHypothesis={
                            gradeAdvancementHypothesesData
                        }
                        excludedGradeAdvancementHypotheses={
                            formState.excludedGradeAdvancementHypotheses
                        }
                        setExcludedGradeAdvancementHypotheses={(v) => {
                            onInputChange(
                                'excludedGradeAdvancementHypotheses',
                                v,
                            );
                        }}
                    />
                </Box>
            )}
        </>
    );
}
